import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip
} from "recharts";
import KC_DashboardCard from "../../../../designsystem/KC_Card/KC_DashboardCard";
import Legend, { LegendI } from "./Legend";
import { Distribution } from "../../model/AnalyticsData";
import { GRAPH_COLORS } from "../../../../designsystem/KC_Card/constants";
import { tt } from "../../../../utils/i18n/i18n";
import GraphWithLegendLayout from "./GraphWithLegendLayout";
import GRAPH_MIN_HEIGHT from "./constants";

interface ChartDatum {
  name: string;
  value: number;
  fill: string;
}

interface Props {
  chartData?: Distribution[];
  title: string;
  useLegend?: boolean;
  colors?: string[];
  getLegendName: (_id: any) => string;
}

export default function GeneralBarChart(props: Props) {
  const transformedData: ChartDatum[] = useMemo(() => {
    if (!props.chartData) {
      return [];
    }
    return props.chartData?.map((item, index) => ({
      name: item._id,
      value: item.count,
      fill: props.colors?.[index] || GRAPH_COLORS[index % GRAPH_COLORS.length]
    }));
  }, [props.chartData, props.colors]);

  const legendData: LegendI[] = useMemo(() => {
    if (!props.useLegend || !transformedData) {
      return [];
    }
    return transformedData.map((datum) => {
      return {
        name: props.getLegendName(datum.name),
        value: datum.value,
        color: datum.fill
      };
    });
  }, [props.useLegend, transformedData]);

  const maxValue = useMemo(() => {
    return Math.max(...transformedData.map((d) => d.value));
  }, [transformedData]);

  const yAxisMax = useMemo(() => {
    return maxValue <= 20
      ? Math.ceil(maxValue / 5) * 5
      : Math.ceil(maxValue / 50) * 50;
  }, [maxValue]);

  const allZeros = useMemo(() => {
    return props.chartData?.every((x) => !x.count) || false;
  }, [props.chartData]);

  function renderLeft() {
    return (
      <div className="d-flex flex-row position-relative flex-grow-1">
        {allZeros && (
          <div
            style={{
              minHeight: GRAPH_MIN_HEIGHT,
              height: "100%",
              width: "100%"
            }}
          >
            <>{tt("analytics_nothing_to_display")}</>
          </div>
        )}
        {!allZeros && (
          <ResponsiveContainer
            width="100%"
            height="100%"
            minHeight={GRAPH_MIN_HEIGHT}
            debounce={500}
          >
            <BarChart
              data={transformedData}
              margin={{ top: 16, right: 16, left: 16, bottom: 16 }}
            >
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={false}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: "#9ca3af" }}
                domain={[0, yAxisMax]}
              />
              <Tooltip cursor={false} />
              <Bar dataKey="value" radius={[8, 8, 8, 8]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }

  return (
    <KC_DashboardCard title={props.title}>
      <GraphWithLegendLayout
        graph={renderLeft()}
        legend={allZeros ? <></> : <Legend data={legendData} />}
      />
    </KC_DashboardCard>
  );
}
